// Müşteri "son gönderim" hafızası — Müşteri Katılımcı Rolü (Task 8).
//
// Neden gerekli: `GET /projects` client aktöre SADECE participantStatus
// 'invited'|'active' olan projeleri döner (services/projects.ts
// listProjectsForClient — Task 5, eng-review D5). Submit edilmiş bir proje
// listeden düşer; dar DTO'da submittedAt/geçmiş de yok. Bu yüzden ClientHome
// "Gönderildi" teşekkür ekranını (design-ref onaylı 3. durum) backend'den tekrar
// sorgulayamaz — submit anında burada localStorage'a yazılan bilgiyle üretir.
//
// Kapsam/sınır: tek cihaz/tarayıcı hafızası. Başka bir cihazdan giriş yapan ya da
// localStorage'ı temizleyen müşteri, submit sonrası relogin'de "Aktif bir form
// daveti yok" (jenerik erişim-yok) ekranını görür — teşekkür mesajı kaybolur ama
// içerik hâlâ DOĞRU (gerçekten doldurulacak form yok). Kalıcı/çok-cihazlı çözüm
// backend'e submittedAt alanı eklenmesini gerektirir (devir — task-8-report.md).
//
// Namespace (Fix Turu 1 — IMPORTANT-1): key kullanıcı id'sine göre ayrılır
// (`mahir.client.lastSubmission.<userId>`). Paylaşılan tarayıcıda Client A submit
// edip çıktıktan sonra Client B (farklı firma kişisi) aynı tarayıcıda login olursa
// B'nin userId'si farklı olduğu için A'nın kaydını OKUYAMAZ — kendi anahtarı boş
// olduğundan jenerik "Aktif bir form daveti yok" ekranını görür. Ek olarak
// auth-store.logout() bu prefix'teki TÜM anahtarları temizler (clearAllClientSubmissions)
// — aynı kullanıcı tekrar login olsa bile önceki oturumdan kalan veri sıfırlanır.

import { type PeriodType } from "@mahir/shared";

const STORAGE_KEY_PREFIX = "mahir.client.lastSubmission";

function storageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}.${userId}`;
}

export interface LastClientSubmission {
  projectId: string;
  frameworkName: string;
  reportingYear: number;
  /** Raporlama dönemi. Bu alan eklenmeden ÖNCE yazılmış kayıtlarda YOK —
   *  okurken "annual"a düşülür (eski kayıtların hepsi yıllık projeydi). */
  periodType: PeriodType;
  submittedAt: string; // ISO — FE'de submit anında yakalanır
}

export function setLastClientSubmission(userId: string, v: LastClientSubmission): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(v));
  } catch {
    // quota vb. — sessiz geç, teşekkür ekranı jenerik "erişim yok"a düşer
  }
}

export function getLastClientSubmission(userId: string): LastClientSubmission | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LastClientSubmission & Partial<LastClientSubmission>;
    return { ...parsed, periodType: parsed.periodType ?? "annual" };
  } catch {
    return null;
  }
}

// Logout'ta çağrılır (auth-store.ts) — prefix'e uyan TÜM kullanıcıların kaydını
// temizler. Sadece "mevcut" user'ı değil hepsini silmemizin nedeni: paylaşılan
// tarayıcıda önceki oturumlardan kalmış olabilecek her türlü kalıntıyı da
// kapsar, tek bir userId'ye güvenmek zorunda kalmayız.
export function clearAllClientSubmissions(): void {
  if (typeof window === "undefined") return;
  try {
    Object.keys(window.localStorage)
      .filter((k) => k.startsWith(STORAGE_KEY_PREFIX))
      .forEach((k) => window.localStorage.removeItem(k));
  } catch {
    // erişilemiyorsa sessiz geç — logout akışını bloklamaz
  }
}
